/* eslint-disable venom/no-venom-api-calls */
import PropTypes from 'prop-types';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { withMetrics } from 'client/data/api/api-metrics';
import { EdmundsAPI, VenomApi } from 'client/data/api/api-client';
import { DEFAULT_LOCATION, Location } from 'client/constants/location';
import { FeatureFlagModel } from 'client/data/models/feature-flag';
import { isNode } from 'client/utils/environment';
import { TIME } from 'client/utils/time';
import { requestContext } from 'client/utils/request-context/request-context';
import { cookie } from 'client/utils/cookie/cookie';
import { featureFlags } from 'client/utils/feature-flags';
import {
  getValidZipStr,
  isValidZip,
  parseDma,
  parseLocation,
  setLocationCookie,
} from 'client/utils/location-context/location';
import { logVariables } from 'client/utils/location-context/instrument';
import { logger } from 'client/utils/logger/logger';
import { ClientConfig } from 'client/configuration';
import { RequestModel } from 'client/data/models/request';
import { shouldIgnoreUrlContext } from 'site-modules/shared/utils/location/should-ignore-url-context';
import { URL_CONTEXT_PATH, UrlModel } from './url';

const PRIVACY_DISCLAIMER_COOKIE_NAME = 'privacy-accept';
const EMAIL_GATE_COOKIE_NAME = 'email-gate-passed';

function setLocation(locationData, cookies, userSet = false) {
  const location = parseLocation(locationData, userSet, DEFAULT_LOCATION, {});
  setLocationCookie(location);
  return location;
}

async function fetchZipLocation(zip, context) {
  return withMetrics(EdmundsAPI, context).fetchJson(`/zip/v2/${zip}?view=basic`);
}

const History = PropTypes.shape({
  wiredLabel: PropTypes.number,
  mobileLabel: PropTypes.number,
  userEvents: PropTypes.arrayOf(
    PropTypes.shape({
      bodyType: PropTypes.string,
      useIn: PropTypes.string,
      pageName: PropTypes.string,
      userId: PropTypes.string,
      timeStamp: PropTypes.number,
      type: PropTypes.string,
      make: PropTypes.string,
      model: PropTypes.string,
      submodel: PropTypes.string,
      year: PropTypes.number,
      styleId: PropTypes.string,
    })
  ),
});

export const VisitorEntities = {
  Location,
  History,
};

export const VisitorModel = createModelSegment('visitor', [
  {
    path: 'rttSegment',
    async resolve(match, context) {
      const disableRttModel = await context.resolveValue('disable-use-rtt-model', FeatureFlagModel);
      if (disableRttModel) {
        return context.abort();
      }

      const userId = await context.resolveValue('id');
      const request = {
        retries: 0,
        method: 'GET',
        credentials: 'same-origin',
      };

      const url = `/user-db/v1/segments/${userId}`;
      return withMetrics(EdmundsAPI, context)
        .fetchJson(url, request)
        .then(userRttSegment => userRttSegment)
        .catch(() => null);
    },
  },
  {
    path: 'location',
    async resolve(match, context) {
      const locationData = requestContext.get('locationData') ?? {};
      const testZip = locationData.testZip;
      const cookies = cookie.get();
      const headers = locationData.headers ?? {};
      const enableDefaultZipLogs = featureFlags.get('enableDefaultZipLogs');

      let cookieData = {
        ipDma: null,
        ipStateCode: null,
        ipCity: null,
        ipZipCode: null,
        userIP: '',
        userSet: false,
        zipCode: '',
      };

      let userSet = false;

      // Attempt to retrieve the user's IP address.
      // If specified, use the locationIP value in the corresponding config/<env>.yaml.
      // Invalid, non-prod, non-IP-V4 addresses will result in an empty string value ('').
      const currentUserIP = locationData.currentUserIP;
      cookieData.userIP = currentUserIP;
      // define current location zip code from location 🍪 or parsed url
      let locationCookie = {
        ...cookies.location,
      };
      userSet = locationCookie && locationCookie.userSet;
      cookieData.userSet = userSet;

      if (userSet) {
        cookieData.zipCode = getValidZipStr(locationCookie.zipCode, cookieData.zipCode);
      }

      // if the location 🍪 doesn't exist OR
      // if the location 🍪's userIP and the current IP don't match
      // then call the ip-to-zip API to retrieve IP-based location data
      if (!locationCookie || locationCookie.userIP !== currentUserIP) {
        try {
          const ipData = await withMetrics(EdmundsAPI, context).fetchJson(`/zip/v2/?ip=${currentUserIP}&view=basic`);

          cookieData.ipZipCode = (ipData && ipData.length && ipData[0].zipCode) || DEFAULT_LOCATION.zipCode;
          cookieData.zipCode = cookieData.zipCode ? cookieData.zipCode : getValidZipStr(cookieData.ipZipCode);
          cookieData.ipDma = parseDma(ipData && ipData.length && ipData[0].dma) || DEFAULT_LOCATION.dma;
          cookieData.ipStateCode = (ipData && ipData.length && ipData[0].stateCode) || DEFAULT_LOCATION.stateCode;
          cookieData.ipCity = ipData?.[0]?.city || DEFAULT_LOCATION.city;

          if (enableDefaultZipLogs && !ipData?.[0]?.zipCode) {
            logVariables('locationContext: the ip-to-zip API did not return a zipCode, default zip code is set', {
              ipZipCodeCookie: cookieData.ipZipCode,
              currentUserIP,
              trueClientIp: headers['true-client-ip'],
            });
          }

          // if the location 🍪 existed, invalidate all data from the previous one
          locationCookie = null;
        } catch (error) {
          // log error
          error['x-client-ip'] = headers['x-client-ip'];
          error['x-forwarded-for'] = headers['x-forwarded-for'];
          error['true-client-ip'] = headers['true-client-ip'];
          error['x-true-client-ip'] = headers['x-true-client-ip'];

          error['x-akamai-edgescape'] = headers['x-akamai-edgescape']; // get more insight into geo location

          logger.error(error);
        }
      } else {
        cookieData.ipDma = locationCookie.ipDma;
        cookieData.ipStateCode = locationCookie.ipStateCode;
        cookieData.ipZipCode = locationCookie.ipZipCode;
        cookieData.ipCity = locationCookie.ipCity;
      }

      const urlContext = await context.resolveValue(URL_CONTEXT_PATH, UrlModel, false);

      const [isSearchBot, isAkamaiCachePrefresh] = [
        context.getValue('isAkamaiCachePrefresh', RequestModel),
        context.getValue('isSearchBot', RequestModel),
      ];

      const useUrlContext =
        urlContext?.location &&
        !shouldIgnoreUrlContext({
          isCrawler: isSearchBot || isAkamaiCachePrefresh,
          urlContextLocation: urlContext.location,
          userLocation: {
            stateCode: locationCookie?.stateCode || cookieData.ipStateCode,
            city: locationCookie?.city || cookieData.ipCity,
          },
        });

      if (useUrlContext) {
        // For certain URLs, a location (city + state) are specified within (e.g., https://www.edmunds.com/used-cars-sacramento-ca/).
        // When a location is specified within the URL, use the location from the url
        // to generate a zip code to store in the EdmundsYear and location cookies.
        const urlLocation = urlContext.location;

        cookieData = {
          ...cookieData,
          ...urlLocation,
        };

        // If the IP address is blank, then set the user's location based on the url that brought them here.
        // If the location context doesn't parse to a valid location, then default values for location will be used.
        // No ipZipCode will be set in the location cookie in the latter instance.
        const valsToUse = currentUserIP ? cookieData : urlLocation;
        if (enableDefaultZipLogs) {
          const matchedLocation = Array.isArray(valsToUse) ? valsToUse[0] : valsToUse;
          if (!isValidZip(matchedLocation?.zipCode)) {
            logVariables(
              `locationContext: default location is set because location (from ${
                currentUserIP ? 'cookies' : 'url context'
              }) doesn't parse to a valid location`,
              { valsToUse, currentUserIP, trueClientIp: headers['true-client-ip'] }
            );
          }
        }

        // forcing userSet=false, since a specific location was chosen from the url, NOT by the user
        return setLocation(valsToUse, cookies, false);
      } else if (testZip) {
        // test-zip query param is used to set/override current location for testing purposes

        cookieData.zipCode = getValidZipStr(testZip, cookieData.zipCode);
        cookieData.userSet = true;
      } else if (locationCookie) {
        // There's no need to modify cookies if we've made it this far and
        // the location 🍪 exist. So we're updating the store and leaving.

        if (enableDefaultZipLogs) {
          const matchedLocation = locationCookie;
          if (!isValidZip(matchedLocation?.zipCode)) {
            logVariables('locationContext: default location is set when both location and EdmundsYear cookies exist', {
              locationCookie,
              currentUserIP,
              trueClientIp: headers['true-client-ip'],
            });
          }
        }

        const location = parseLocation(locationCookie, !!locationCookie.userSet, DEFAULT_LOCATION, {});
        return location;
      }

      // if edmunds API url is not defined, default location is set
      // if ip is undefined, default location is set
      const configEdmundsApiUrl = ClientConfig.get('edmundsApiUrl');
      if (!configEdmundsApiUrl || !currentUserIP) {
        if (enableDefaultZipLogs) {
          logVariables(
            'locationContext: default location is set when edmunds API url is not defined or ip is undefined',
            {
              edmundsApiUrl: configEdmundsApiUrl,
              currentUserIP,
              trueClientIp: headers['true-client-ip'],
            }
          );
        }

        return setLocation();
      }

      // If we've made it this far, all values related to location are available
      // and it's time to create some cookies.
      try {
        const zipData = await withMetrics(EdmundsAPI, context).fetchJson(`/zip/v2/${cookieData.zipCode}?view=basic`);

        return setLocation({ ...zipData, ...cookieData }, cookies, cookieData.userSet);
      } catch (error) {
        // log error first
        logger.error(error);

        if (enableDefaultZipLogs) {
          logVariables('locationContext: default location is set in case of error', {
            errorMessage: error.message,
            cookieZipCode: cookieData.zipCode,
          });
        }

        // default location is set in case of error
        return setLocation();
      }
    },
    async update({ zipCode, latitude, longitude, location, $ref } = {}, match, context) {
      let retValue;
      if ($ref) {
        retValue = { $ref };
      } else if (location) {
        retValue = location;
      } else {
        let url;

        if (zipCode) {
          url = `/location/zip/${zipCode}`;
        } else if (latitude && longitude) {
          url = `/location/geo/${latitude},${longitude}`;
        } else {
          return Promise.reject('The zip code or latitude and longitude are required.');
        }

        const response = await VenomApi.fetchJson(url, { credentials: 'same-origin' });
        if (!(response.success && response.location && response.location.zipCode)) {
          return Promise.reject('Invalid location received.');
        }
        retValue = response.location;
      }

      const urlContext = await context.resolveValue('context', UrlModel, false);
      const isNational = urlContext?.attributes?.nationalScope;
      if (isNational) {
        await context.updateValue('context.attributes.nationalScope', false, UrlModel);
      }

      return retValue;
    },
  },
  {
    path: 'locationByZip["{zip}"]',
    async resolve({ zip }, context) {
      if (!isValidZip(zip)) {
        const result = parseLocation(undefined, false, DEFAULT_LOCATION, {});
        await context.updateValue('location', { $ref: `#/locationByZip/${zip}`, location: result }, VisitorModel);
        return result;
      }

      const cookieLocation = cookie.get().location;
      let location;
      if (cookieLocation?.zipCode === zip) {
        location = cookieLocation;
      } else {
        location = await fetchZipLocation(zip, context);
      }
      const result = parseLocation(location || null, !!location, DEFAULT_LOCATION, {});

      await context.updateValue('location', { location: result }, VisitorModel); // PLAT-4694 workaround
      // todo replace with await context.updateValue('location', { $ref: `#/locationByZip/${zip}` }, VisitorModel); when PLAT-4694 is fixed

      return result;
    },
  },
  {
    path: 'location.address',
    resolve(match, context) {
      return context.resolveValue('location');
    },
    async update({ zipCode, latitude, longitude } = {}) {
      let url;

      if (zipCode) {
        url = `/location/zip/${zipCode}`;
      } else if (latitude && longitude) {
        url = `/location/geo/${latitude},${longitude}`;
      }

      if (!url) return Promise.reject('The zip code or latitude and longitude are required.');

      const response = await VenomApi.fetchJson(url, { credentials: 'same-origin' });

      if (!(response.success && response.location && response.location.zipCode)) {
        return Promise.reject('Invalid location received.');
      }

      return response.location;
    },
  },
  {
    path: 'privacy',
    async update({ doNotSell }) {
      if (doNotSell === undefined) return Promise.reject('The doNotSell is required.');

      const url = '/privacy/';
      const response = await VenomApi.fetchJson(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ doNotSell }),
      });
      return response;
    },
  },
  {
    path: 'privacyAccepted',
    async resolve() {
      if (isNode()) {
        return Promise.resolve(undefined);
      }

      const cookieVal = document.cookie.match(`(^|;) ?${PRIVACY_DISCLAIMER_COOKIE_NAME}=([^;]*)(;|$)`);
      return Promise.resolve(cookieVal?.[2] === 'true');
    },
    update(value) {
      cookie.set(PRIVACY_DISCLAIMER_COOKIE_NAME, value, {
        path: '/',
        maxAge: TIME.MS.TEN_YEARS,
      });

      return Promise.resolve(value === 'true');
    },
  },
  {
    path: 'emailGatePassed',
    async resolve() {
      if (isNode()) {
        return Promise.resolve(undefined);
      }

      const cookieVal = document.cookie.match(`(^|;) ?${EMAIL_GATE_COOKIE_NAME}=([^;]*)(;|$)`);
      return Promise.resolve(cookieVal?.[2] === 'true');
    },
    update(value) {
      cookie.set(EMAIL_GATE_COOKIE_NAME, value, {
        path: '/',
        maxAge: TIME.MS.TEN_YEARS,
      });

      return Promise.resolve(value === 'true');
    },
  },
]);
